import { useMemo } from 'react';
import { useCampaigns } from '../../lib/api/campaigns';
import { parseDate, toISODate } from '../../lib/dates';
import { HeatmapPicker, DayDensity } from './HeatmapPicker';

interface Props {
  value: string;
  onPick: (iso: string) => void;
  /** Don't count the campaign we're editing against itself. */
  excludeCampaignId?: string;
}

/** Heatmap picker for campaign start dates — colours each day by how many
 *  campaigns are running that day. Hover lists the campaign names so you
 *  can spot direct conflicts beyond pure density. */
export const CampaignDateHeatmapPicker = ({ value, onPick, excludeCampaignId }: Props) => {
  const { data: campaigns = [] } = useCampaigns();

  const dayItems = useMemo(() => {
    const m = new Map<string, DayDensity>();
    for (const c of campaigns) {
      if (excludeCampaignId && c.id === excludeCampaignId) continue;
      if (!c.start_date || !c.end_date) continue;
      const start = parseDate(c.start_date);
      const end = parseDate(c.end_date);
      const cursor = new Date(start);
      while (cursor <= end) {
        const iso = toISODate(cursor);
        const existing = m.get(iso) ?? { count: 0, labels: [] as string[] };
        existing.count += 1;
        existing.labels?.push(c.name);
        m.set(iso, existing);
        cursor.setDate(cursor.getDate() + 1);
      }
    }
    return m;
  }, [campaigns, excludeCampaignId]);

  return (
    <HeatmapPicker
      value={value}
      onPick={onPick}
      dayItems={dayItems}
      triggerLabel="See campaigns"
      triggerTooltip="See which campaigns are running to spot conflicts"
      countNoun="running"
    />
  );
};

export default CampaignDateHeatmapPicker;
